import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BsFillShareFill, BsArrowLeft } from 'react-icons/bs';
import axios from 'axios';
import { API_BASE_URL, FILE_BASE_URL, SITE_BASE_URL } from '../apiConfig';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Calendar, Clock, Bookmark, ChevronLeft, Share2 } from 'lucide-react';
import useBreakpoint from '../hooks/useBreakpoint';

export default function News() {
    window.scrollTo(0, 0);
    const navigate = useNavigate();
    const { id } = useParams();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const { isMobile, isTablet } = useBreakpoint();

    // Progress bar logic
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/newsDetails/${id}`).then((res) => {
            setNews(res.data.news);
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    }, [id]);

    const handleShare = () => {
        const url = `${SITE_BASE_URL}/news/${id}`;
        if (navigator.share) {
            navigator.share({
                title: news.title,
                url: url
            }).catch(console.error);
        } else {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        }
    };

    const styles = {
        wrapper: {
            width: '100%',
            background: '#ffffff',
            minHeight: '100vh',
            position: 'relative',
            overflowX: 'hidden'
        },
        progressBar: {
            position: 'fixed',
            top: isMobile ? '70px' : '112px',
            left: 0,
            right: 0,
            height: '4px',
            background: 'var(--yellow)',
            transformOrigin: '0%',
            zIndex: 100
        },
        heroSection: {
            width: '100%',
            height: isMobile ? '70vh' : '90vh',
            minHeight: '500px',
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            paddingBottom: isMobile ? '40px' : '80px',
            background: '#040960' // Fallback
        },
        heroBg: {
            position: 'absolute',
            inset: 0,
            backgroundImage: news ? `url("${FILE_BASE_URL}/${news.image}")` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0
        },
        heroOverlay: {
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(4, 9, 96, 0.95) 0%, rgba(4, 9, 96, 0.4) 50%, rgba(0,0,0,0.2) 100%)',
            zIndex: 1
        },
        contentContainer: {
            maxWidth: '1280px',
            margin: '0 auto',
            padding: isMobile ? '0 20px' : '0 40px',
            position: 'relative',
            zIndex: 2,
            width: '100%',
            boxSizing: 'border-box'
        },
        headline: {
            fontFamily: 'var(--fontAnton)',
            fontSize: isMobile ? '36px' : 'clamp(48px, 6vw, 84px)',
            lineHeight: 1.05,
            color: '#FFFFFF',
            textTransform: 'uppercase',
            margin: '0 0 24px 0',
            maxWidth: '1000px',
            textShadow: '0 10px 30px rgba(0,0,0,0.3)'
        },
        metaCard: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '2px', // Sharp corners for broadcast feel
            padding: isMobile ? '16px' : '20px 32px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: isMobile ? '20px' : '40px',
            color: '#FFFFFF',
            flexWrap: 'wrap'
        },
        metaItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontFamily: 'var(--fontRajdhani)',
            fontSize: '16px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        mainBody: {
            maxWidth: '820px',
            margin: '-40px auto 100px',
            background: '#FFFFFF',
            padding: isMobile ? '40px 20px' : '80px 100px',
            position: 'relative',
            zIndex: 3,
            boxShadow: '0 50px 100px rgba(0,0,0,0.05)',
            boxSizing: 'border-box'
        },
        articleContent: {
            fontFamily: 'var(--fontInter)',
            fontSize: isMobile ? '16px' : '19px',
            lineHeight: 1.9,
            color: '#1a1a1a',
            fontWeight: 400
        },
        backButton: {
            position: 'absolute',
            top: isMobile ? '90px' : '140px',
            left: isMobile ? '20px' : '40px',
            zIndex: 50,
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '40px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'var(--fontRajdhani)',
            fontWeight: 800,
            fontSize: '14px',
            textTransform: 'uppercase',
            transition: 'all 0.3s ease'
        },
        shareFab: {
            width: '48px',
            height: '48px',
            background: 'var(--yellow)',
            color: 'var(--blue)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 10px 20px rgba(231,196,36,0.3)',
            transition: 'transform 0.2s',
        }
    };

    if (loading) return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--blue)' }}>
            <div className="loader" style={{ borderColor: 'var(--yellow)', borderTopColor: 'transparent' }}></div>
        </div>
    );

    if (!news) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>News not found</div>;

    return (
        <div style={styles.wrapper}>
            <motion.div style={styles.progressBar} style={{ scaleX }} />

            {/* Float Back Button */}
            <motion.button 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                style={styles.backButton}
                onClick={() => navigate(-1)}
                whileHover={{ background: 'var(--yellow)', color: 'var(--blue)', scale: 1.05 }}
            >
                <ChevronLeft size={18} /> BACK
            </motion.button>

            {/* Immersive Hero */}
            <section style={styles.heroSection}>
                <motion.div 
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                    style={styles.heroBg} 
                />
                <div style={styles.heroOverlay} />
                
                <div style={styles.contentContainer}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        <span style={{ 
                            color: 'var(--yellow)', 
                            fontFamily: 'var(--fontRajdhani)', 
                            fontWeight: 800, 
                            fontSize: '16px', 
                            letterSpacing: '4px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '16px'
                        }}>LATEST NEWS</span>
                        <h1 style={styles.headline}>{news.title}</h1>
                        
                        <div style={styles.metaCard}>
                            <div style={styles.metaItem}>
                                <Calendar size={18} color="var(--yellow)" />
                                <span>{news.publish_date}</span>
                            </div>
                            <div style={styles.metaItem}>
                                <Clock size={18} color="var(--yellow)" />
                                <span>5 MIN READ</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <motion.button 
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={handleShare}
                                    style={styles.shareFab}
                                >
                                    <Share2 size={20} />
                                </motion.button>
                                <span style={styles.metaItem}>SHARE</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Article Content */}
            <motion.main 
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                style={styles.mainBody}
            >
                {/* Visual Accent */}
                <div style={{ 
                    width: '60px', 
                    height: '4px', 
                    background: 'var(--blue)', 
                    marginBottom: '40px' 
                }} />

                <div 
                    style={styles.articleContent}
                    dangerouslySetInnerHTML={{ __html: news.content }}
                />

                <footer style={{ marginTop: '80px', paddingTop: '40px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', background: 'var(--blue)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Bookmark size={20} color="#fff" />
                        </div>
                        <div>
                            <span style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#999', textTransform: 'uppercase' }}>Author</span>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase' }}>BTCL Media</span>
                        </div>
                    </div>
                </footer>
            </motion.main>

            <style>{`
                .article-body p { margin-bottom: 2em; }
                .article-body h2, .article-body h3 { 
                    font-family: var(--fontAnton); 
                    text-transform: uppercase; 
                    margin: 2em 0 1em; 
                    color: var(--blue);
                }
                @media (max-width: 768px) {
                    .main-body-revamp {
                        margin-top: 0 !important;
                        box-shadow: none !important;
                    }
                }
            `}</style>
        </div>
    );
}